import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useAuth } from '@/contexts/AuthContext';
import { useRecurringSeriesList, useRecurringTeamMembers } from '@/hooks/useRecurringSeries';
import {
  AdminPage,
  FilterChip,
  PageHeader,
  Panel,
  StatCard,
  Toolbar,
  ToolbarSpacer,
  adminButton,
  adminInput,
  useAdminPageTitle,
} from '@/components/admin/ui';
import { RepeatCleansList } from '@/components/admin/recurring/RepeatCleansList';
import type { SeriesStatus } from '@/lib/recurrence';
import { cn } from '@/lib/utils';

const FILTERS: { key: SeriesStatus | 'all'; label: string }[] = [
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Awaiting approval' },
  { key: 'paused', label: 'Paused' },
  { key: 'ended', label: 'Ended' },
  { key: 'all', label: 'All' },
];

/** Admin › Repeat cleans: every recurring series for the business. */
const RepeatCleansAdmin = () => {
  useAdminPageTitle('Repeat cleans');
  const { orgId, canWrite: orgCanWrite } = useAdminOrg();
  const { staffRole } = useAuth();
  // Managing repeat cleans is for owners/supervisors (the RPCs enforce it too).
  const canWrite = orgCanWrite && staffRole !== 'cleaner';
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('series');
  const { data: rows = [], isLoading } = useRecurringSeriesList(orgId);
  const { data: teamMembers = [] } = useRecurringTeamMembers(orgId);
  const [filter, setFilter] = useState<SeriesStatus | 'all'>(highlightId ? 'all' : 'active');
  const [search, setSearch] = useState('');

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    rows.forEach((r) => (c[r.status] = (c[r.status] ?? 0) + 1));
    return c;
  }, [rows]);

  const clashCount = rows.reduce((n, r) => n + r.clashes.length, 0);
  const upcoming = rows.reduce((n, r) => n + (r.status === 'active' ? r.upcomingCount : 0), 0);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter((r) => filter === 'all' || r.status === filter)
      .filter(
        (r) =>
          !term ||
          [r.client_name, r.client_email, r.title, r.address, ...r.cleanerNames]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(term)),
      );
  }, [rows, filter, search]);

  return (
    <AdminPage>
      <PageHeader
        description="Regular cleans that book themselves. Visits are added 12 weeks ahead and topped up every day."
        actions={
          canWrite && (
            <Button asChild size="sm" className={adminButton.primary}>
              <Link to="/admin/schedule?new=job">
                <Plus /> New repeat clean
              </Link>
            </Button>
          )
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Active repeat cleans" value={counts.active ?? 0} />
        <StatCard label="Upcoming visits booked" value={upcoming} />
        <StatCard label="Dates needing attention" value={clashCount} />
      </div>

      <Panel padded={false}>
        <div className="border-b border-border px-4 py-3 md:px-5">
          <Toolbar>
            {FILTERS.map((f) => (
              <FilterChip key={f.key} active={filter === f.key} count={counts[f.key] ?? 0} onClick={() => setFilter(f.key)}>
                {f.label}
              </FilterChip>
            ))}
            <ToolbarSpacer />
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search customer or cleaner"
                aria-label="Search repeat cleans"
                className={cn(adminInput, 'pl-8')}
              />
            </div>
          </Toolbar>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <RepeatCleansList
            rows={visible}
            teamMembers={teamMembers}
            canWrite={canWrite}
            highlightId={highlightId}
            emptyTitle={rows.length === 0 ? 'No repeat cleans yet.' : 'Nothing matches this filter.'}
          />
        )}
      </Panel>
    </AdminPage>
  );
};

export default RepeatCleansAdmin;
